/* eslint-disable quote-props */
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from '@michelin/central-provider';
import {
  Customer, DealerProfileLocation, GlobalTextInputProps, HoursOfOperation, POSDetails,
} from 'Types';
import { GooglePlacesInfo, GooglePlacesMapInfo } from 'Types/GooglePlaces';
import { Grid, InputAdornment, IconButton } from '@material-ui/core';
import { Email } from '@material-ui/icons';
import {
  Panel,
  TextField,
  PhoneInput,
  Switch,
  useDialogs,
  PreventTransitionPrompt,
  isPossiblePhoneNumber,
  Select,
} from '@michelin/acid-components';
import { getOptions } from '@michelin/select-options-provider';
import ListingFilterSelect from '@michelin/acid-components/build/components/ListingFilters/ListingFilterSelect';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import { useSaveRetailLocation } from 'hooks/useLocation';
import { useSaveGeoRetailLocation } from 'hooks/useGeoLocation';
import useUtils from 'hooks/useUtils';
import HoursOfOperations from 'components/HoursOfOperation/Index';
import { CheckboxGroup, CheckBoxItem } from 'components/CommercialServices/CheckboxGroup';
import { useSnackbar } from 'notistack';
import TextFieldUOM, { UOM } from '@michelin/acid-components/build/components/TextFieldUOM/TextFieldUOM';
import { GenericActionButtons } from 'components/GenericActionButtons/GenericActionButtons';
import { getCountryCode, scrollToDisplayFirstError } from 'Utils';
import POSInfoIcon from 'components/POS/POSInfoIcon';
import GooglePlaces, { OnChangeIds } from '../GooglePlaces';
import POS from '../POS/POS';

export function checkCRetailMaxServiceRadius(value: string): string {
  // max should be 2800 miles but in meters (that's US width)
  const max = 4506163;
  return (Math.max(0, Math.min(max, parseFloat(value))) || '').toString();
}

export function checkCommercialMaxServiceRadius(value: string, countryCode:string): string {
  if (value === '' || value === null || value === undefined) return '';
  // max should be 50 miles but in meters for US and 80 km for CA.
  const max = countryCode.toLocaleLowerCase() === 'us' ? 80467.2 : 80000;
  let res = (Math.max(0, Math.min(max, parseFloat(value))) || '').toString();
  // if res is 30 miles and we're not in US, we need to set it to 49 km
  if (countryCode.toLocaleLowerCase() !== 'us' && res.replace(',', '.').split('.')[0] === '48280') {
    res = '49000';
  }
  return res;
}

export function checkCommercialMinServiceRadius(value: string, countryCode:string): string {
  if (value === '' || value === null || value === undefined) return '';
  // min should be 30 miles but in meters for US and 49 km for CA.
  const min = countryCode.toLocaleLowerCase() === 'us' ? 48280.3 : 49000;
  return (Math.max(min, parseFloat(value)) || '').toString();
}

const readOnlyTextInputProps: GlobalTextInputProps = {
  variant: 'standard',
  InputProps: { readOnly: true },
  style: { display: 'flex', flexWrap: 'wrap' },
};

const brandsLabels = {
  'sells_michelin': 'Michelin',
  'sells_bfgoodrich': 'BFGoodrich',
  'sells_uniroyal': 'Uniroyal',
};

function LocationPanel(props: LocationDetailsProps) {
  const { t } = useTranslation();
  const { emailRegex, urlRegex } = useUtils();
  const { btNumber, stNumber } = useParams<{
    stNumber: string;
    btNumber: string;
  }>();
  const { errorDialog } = useDialogs();
  const permissions = usePermissionsSPP();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { customer } = props;
  const readOnly: boolean = props.action === 'view';
  const defaultReadOnlyText = '';

  const textInputProps: GlobalTextInputProps = {
    variant: 'standard',
    InputProps: { readOnly },
    style: { display: 'flex', flexWrap: 'wrap' },
  };
  const brandsItems: CheckBoxItem<any>[] = [
    { key: 'sells_michelin', disabled: true },
    { key: 'sells_bfgoodrich', disabled: true },
    { key: 'sells_uniroyal', disabled: true },
  ];
  const [googlePlacesInfo, setGooglePlacesInfo] = useState<GooglePlacesInfo | null>(null);
  const {
    formState,
    getValues,
    setValue,
    control,
    watch,
    reset,
    handleSubmit,
  } = useForm<any>({ defaultValues: props.location });
  const country = getCountryCode(getValues(), customer);
  const mutateLocation = useSaveRetailLocation(stNumber);
  const mutateGeoLocation = useSaveGeoRetailLocation(stNumber);

  const onSave = async (data: DealerProfileLocation) => {
    // eslint-disable-next-line no-throw-literal
    if (!data) throw { error: 'No data' };
    await mutateLocation.mutateAsync({ stNumber, data });
    reset(getValues());
    history.push('view');
    enqueueSnackbar(t('Location information saved.'), {
      variant: 'success',
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
    });
  };

  const onSaveGeoLocation = async (data: any) => {
    try {
      await mutateGeoLocation.mutateAsync({ stNumber, data });
      reset();
      history.push('view');
    } catch (e) {
      errorDialog(
        t('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n'),
        t('Error saving Location Data.'),
      );
    }
  };

  useEffect(() => {
    reset(props.location);
  }, [props.location, reset]);

  useEffect(() => {
    if (props.action === 'view') reset();
  }, [props.action, reset]);

  const onError = () => {
    errorDialog(t('Please check the marked fields.').split('\n'), t('Error saving Location Data.'));
    scrollToDisplayFirstError();
  };

  const save = async () => {
    if (!formState.isDirty) return;
    if (!getValues()) return;

    if (getValues('headquarters_flag') && getValues('has_pos_system') && !getValues('pos_details.pos_provider')) {
      onError();
      return;
    }

    if (getValues('headquarters_flag') && getValues('has_pos_system') && getValues('pos_details.pos_provider') === 'OTHER' && !getValues('pos_details.other_name')) {
      onError();
      return;
    }

    try {
      const newData: DealerProfileLocation = {} as DealerProfileLocation;
      if (getValues('google_places.requested_geo_code_update')) {
        newData.requested_geo_code_latitude = getValues('google_places.requested_geo_code_latitude');
        newData.requested_geo_code_longitude = getValues('google_places.requested_geo_code_longitude');
        newData.requested_geo_code_update = getValues('google_places.requested_geo_code_update');
        newData.requested_geo_code_explanation = getValues('google_places.requested_geo_code_explanation');
        if (!newData.requested_geo_code_explanation) {
          onError();
          return;
        }
      } else {
        newData.requested_geo_code_latitude = undefined;
        newData.requested_geo_code_longitude = undefined;
        newData.requested_geo_code_update = false;
        newData.requested_geo_code_explanation = undefined;
      }
      newData.retail_service_radius = getValues('retail_service_radius');
      newData.has_pos_system = !!getValues('has_pos_system');
      newData.pos_details = (newData.has_pos_system && getValues('pos_details')) ? getValues('pos_details') : {} as POSDetails;
      if (getValues('hours_of_operation')) {
        newData.hours_of_operation = { ...getValues('hours_of_operation') };
      }
      newData.google_places_id = ((getValues('google_places.google_places_id') || '').length) ? getValues('google_places.google_places_id') : null;
      newData.toll_free_number = ((getValues('toll_free_number') || '').length) ? getValues('toll_free_number') : null;
      newData.after_hours_phone_number = ((getValues('after_hours_phone_number') || '').length) ? getValues('after_hours_phone_number') : null;
      newData.store_email = ((getValues('store_email') || '').length) ? getValues('store_email') : null;
      newData.site_url = ((getValues('site_url') || '').length) ? getValues('site_url') : null;
      newData.pos_type = (getValues('pos_type') || '').length ? getValues('pos_type') : null;
      newData.open_for_business = getValues('open_for_business') || false;
      newData.mobile_only = getValues('mobile_only') || false;

      await onSave(newData as DealerProfileLocation);
    } catch (e) {
      errorDialog(
        [
          ...(e.toString()
            ? [`${t(e.toString())}`]
            : ('Please check your connection and try again.\nIf the issue persist, please contact support').split('\n')),
        ],
        t('Error saving Location Data.'),
      );
    }
  };

  const renderMap = (): React.ReactElement | null => {
    if (!customer) return null;
    if (!getValues()) return null;

    return (
      <Controller
        name="google_places"
        control={control}
        render={(item) => (
          <GooglePlaces
            customer_name={customer.customer_name || ''}
            googlePlacesMapInfo={item.field.value || {} as GooglePlacesMapInfo}
            onChange={(fieldId: OnChangeIds, fieldValue: any, value: GooglePlacesMapInfo) => {
              item.field.onChange(value);
            }}
            googlePlacesInfo={googlePlacesInfo || undefined}
            googlePlacesInfoOnChange={(newGooglePlacesInfo: GooglePlacesInfo) => {
              setGooglePlacesInfo(newGooglePlacesInfo);
            }}
            inputProps={textInputProps}
            acceptLocationShow={permissions.allowsAction('location_information.modify_lat_long_coordinates')}
            acceptLocationOnSave={(geoData: any) => {
              onSaveGeoLocation(geoData);
            }}
            readOnly={readOnly}
            errors={{} as any}
          />
        )}
      />
    );
  };

  const renderPhysicalAddressInformationPanel = (): React.ReactElement | null => {
    if (!customer) return null;
    if (!getValues()) return null;

    return (
      <Panel title={t('Physical Address Information')}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  variant={readOnlyTextInputProps.variant}
                  InputProps={readOnlyTextInputProps.InputProps}
                  style={readOnlyTextInputProps.style}
                  label={t('Legal Ship To Name')}
                  value={customer.customer_name || defaultReadOnlyText}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  variant={readOnlyTextInputProps.variant}
                  InputProps={readOnlyTextInputProps.InputProps}
                  style={readOnlyTextInputProps.style}
                  id="dba_name"
                  label={country === 'ca' ? t('Operated as') : t('DBA Name')}
                  value={customer.customer_dba_name || defaultReadOnlyText}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  variant={readOnlyTextInputProps.variant}
                  InputProps={readOnlyTextInputProps.InputProps}
                  style={readOnlyTextInputProps.style}
                  id="customer_addr1"
                  label={t('Address 1')}
                  value={customer.customer_addr1 || defaultReadOnlyText}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  variant={readOnlyTextInputProps.variant}
                  InputProps={readOnlyTextInputProps.InputProps}
                  style={readOnlyTextInputProps.style}
                  id="customer_addr2"
                  label={t('Address 2')}
                  value={customer.customer_addr2 || defaultReadOnlyText}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  variant={readOnlyTextInputProps.variant}
                  InputProps={readOnlyTextInputProps.InputProps}
                  style={readOnlyTextInputProps.style}
                  id="mc.customer_country"
                  label={t('Country')}
                  value={t(`COUNTRY_CODE|${customer.customer_country}`) || defaultReadOnlyText}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  variant={readOnlyTextInputProps.variant}
                  InputProps={readOnlyTextInputProps.InputProps}
                  style={readOnlyTextInputProps.style}
                  id="mc.customer_city"
                  label={t('City')}
                  value={customer.customer_city || defaultReadOnlyText}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ListingFilterSelect
                  id="mc.customer_state"
                  value={customer.customer_state || defaultReadOnlyText}
                  variant="standard"
                  title={country === 'ca' ? t('Province') : t('State')}
                  options={country === 'ca' ? getOptions('ca_provinces') : getOptions('us_states')}
                  readOnly
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  variant={readOnlyTextInputProps.variant}
                  InputProps={readOnlyTextInputProps.InputProps}
                  style={readOnlyTextInputProps.style}
                  id="mc.customer_zip"
                  label={country === 'ca' ? t('Postal Code') : t('ZIP')}
                  value={customer.customer_zip || defaultReadOnlyText}
                />
              </Grid>
              {country === 'ca' ? null
                : (
                  <Grid item xs={12} md={6}>
                    <TextField
                      variant={readOnlyTextInputProps.variant}
                      InputProps={readOnlyTextInputProps.InputProps}
                      style={readOnlyTextInputProps.style}
                      id="county"
                      label={t('County')}
                      value={customer.customer_county || defaultReadOnlyText}
                    />
                  </Grid>
                )}
              <Grid item xs={12}>
                <CheckboxGroup
                  getValues={getValues}
                  disableGroupSelector
                  control={control}
                  cols={4}
                  labels={{
                    retail_location_flag: 'Retail Location',
                    headquarters_flag: 'Headquarters Location',
                    warehouse_flag: 'Warehouse Location',
                  }}
                  readOnly
                  setValue={setValue}
                  watch={watch}
                  items={[
                    { key: 'retail_location_flag', disabled: true },
                    { key: 'headquarters_flag', disabled: true },
                    { key: 'warehouse_flag', disabled: true },
                  ]}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="retail_service_radius"
                  control={control}
                  render={(item) => (
                    <TextFieldUOM
                      id="service_radius"
                      variant="standard"
                      style={{ display: 'flex', flexWrap: 'wrap' }}
                      uom={country === 'us' ? UOM.miles : UOM.km}
                      onChange={(newValue: string) => item.field.onChange(checkCRetailMaxServiceRadius(newValue))}
                      value={item.field.value?.toString() || ''}
                      label={t('Service Radius')}
                      decimals={2}
                      min={0}
                      step={1}
                      readOnly={readOnly}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4} style={{ paddingTop: 20 }}>
                <Controller
                  name="mobile_only"
                  control={control}
                  render={(item) => (
                    <Switch
                      label={t('Mobile Only')}
                      labelPosition="right"
                      hideLabel
                      readOnly={readOnly}
                      disabled={!permissions.allowsAction('update_open_for_business.update')}
                      checked={item.field.value === true}
                      color="primary"
                      onChange={(event, newValue) => item.field.onChange(newValue)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={5} style={{ padding: 17, paddingTop: 8 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} style={{ marginBottom: 7 }}>
                {renderMap()}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Panel>
    );
  };

  const renderContactInformationPanel = (): React.ReactElement | null => {
    if (!customer) return null;

    return (
      <Panel title={t('Contact Information')}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Controller
              name="st_phone"
              control={control}
              render={(item) => (
                <PhoneInput
                  label={t('Ship To Phone Number')}
                  value={item.field.value || defaultReadOnlyText}
                  defaultCountry={(country === 'ca' ? 'CA' : 'US')}
                  readOnly
                  required={false}
                  onChange={(e) => item.field.onChange(e.value)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="toll_free_number"
              control={control}
              rules={{ validate: (value: string) => isPossiblePhoneNumber(value) || value === '' }}
              render={(item) => (
                <PhoneInput
                  label={t('Toll Free Phone Number')}
                  value={item.field.value || defaultReadOnlyText}
                  defaultCountry={(country === 'ca' ? 'CA' : 'US')}
                  readOnly={readOnly}
                  required={false}
                  onChange={(e) => item.field.onChange(e.value)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="after_hours_phone_number"
              control={control}
              rules={{ validate: (value: string) => isPossiblePhoneNumber(value) || value === '' }}
              render={(item) => (
                <PhoneInput
                  label={t('After Hours Phone Number')}
                  value={item.field.value || defaultReadOnlyText}
                  defaultCountry={(country === 'ca' ? 'CA' : 'US')}
                  readOnly={readOnly}
                  required={false}
                  onChange={(e) => item.field.onChange(e.value)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container alignItems="flex-end">
              <Controller
                name="store_email"
                control={control}
                rules={{ pattern: emailRegex }}
                render={(item) => (
                  <TextField
                    label={t('Store Email')}
                    value={item.field.value || defaultReadOnlyText}
                    variant={textInputProps.variant}
                    style={textInputProps.style}
                    onChange={(e) => item.field.onChange(e.target.value)}
                    error={!!item.formState.errors.store_email}
                    readOnly={readOnly}
                    InputProps={{
                      readOnly,
                      startAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            aria-label={t('Store Email')}
                            disabled
                          >
                            <Email />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="site_url"
              control={control}
              rules={{ pattern: urlRegex }}
              render={(item) => (
                <TextField
                  label={t('Website Address')}
                  value={item.field.value || defaultReadOnlyText}
                  variant={textInputProps.variant}
                  style={textInputProps.style}
                  readOnly={readOnly}
                  required={false}
                  onChange={(e) => item.field.onChange(e.target.value)}
                  error={!!item.formState.errors.site_url}
                />
              )}
            />
          </Grid>
        </Grid>
      </Panel>
    );
  };

  const renderMailingAddressInformationPanel = (): React.ReactElement | null => (
    <Panel title={t('Mailing Address Information')}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            id="mailing_address1"
            label={t('Address 1')}
            value={props.location.mailing_address1 || defaultReadOnlyText}
            variant={readOnlyTextInputProps.variant}
            style={readOnlyTextInputProps.style}
            readOnly
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id="mailing_address2"
            label={t('Address 2')}
            value={props.location.mailing_address2 || defaultReadOnlyText}
            variant={readOnlyTextInputProps.variant}
            style={readOnlyTextInputProps.style}
            readOnly
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            id="mailing_city"
            label={t('City')}
            value={props.location.mailing_city || defaultReadOnlyText}
            variant={readOnlyTextInputProps.variant}
            style={readOnlyTextInputProps.style}
            readOnly
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ListingFilterSelect
            id="mailing_state"
            value={props.location.mailing_state || defaultReadOnlyText}
            title={country === 'ca' ? t('Province') : t('State')}
            options={country === 'ca' ? getOptions('ca_provinces') : getOptions('us_states')}
            variant={readOnlyTextInputProps.variant}
            readOnly
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            id="mailing_zip"
            label={country === 'ca' ? t('Postal Code') : t('ZIP')}
            value={props.location.mailing_zip || defaultReadOnlyText}
            variant={readOnlyTextInputProps.variant}
            style={readOnlyTextInputProps.style}
            readOnly
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            id="mailing_country"
            label={t('Country')}
            value={props.location.mailing_country || defaultReadOnlyText}
            variant={readOnlyTextInputProps.variant}
            style={readOnlyTextInputProps.style}
            readOnly
          />
        </Grid>
      </Grid>
    </Panel>
  );

  const renderPOSDetails = () => {
    if (!getValues('headquarters_flag')) return null;
    const headquartersFlag = watch('headquarters_flag');
    if (!headquartersFlag) return null;
    const hasPosSystem = watch('has_pos_system');

    return (
      <Panel title={t('POS')}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="has_pos_system"
              control={control}
              render={(item) => (
                <Switch
                  labelPosition="left"
                  label={t('Do you have a POS System?')}
                  uncheckedLabel={t('No')}
                  checkedLabel={t('Yes')}
                  readOnly={readOnly}
                  checked={item.field.value === true}
                  onChange={(event, newValue) => {
                    item.field.onChange(newValue);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            {(hasPosSystem ? (
              <Controller
                name="pos_details"
                control={control}
                render={(item) => (
                  <POS
                    shipToNumber={stNumber}
                    billToNumber={btNumber}
                    countryCode={country || ''}
                    action={props.action}
                    posDetails={item.field.value || {} as POSDetails}
                    validationErrors={{}}
                    onChange={(posDetails) => {
                      item.field.onChange(posDetails);
                    }}
                  />
                )}
              />
            ) : null)}
          </Grid>
        </Grid>
      </Panel>
    );
  };

  const renderMichelinIntegrations = () => (
    <Panel title={t('Michelin Integrations')}>
      <Grid container spacing={2}>
        <POSInfoIcon />
        <Grid item xs={12}>
          <Controller
            name="pos_type"
            control={control}
            render={(item) => (
              <Select
                id={item.field.name}
                name="pos_type"
                data-testid="pos_type-test"
                label={t('POS Integration')}
                value={item.field.value}
                style={{ width: '100%' }}
                readOnly={readOnly || !permissions.allowsAction('michelin_integrations.update_pos_integration')}
                required
                options={getOptions('pos_type')}
                onChange={(e) => item.field.onChange(e.target.value)}
                error={!!formState.errors.pos_type}
              />
            )}
          />
        </Grid>
      </Grid>
    </Panel>
  );

  const renderBrandsSold = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <CheckboxGroup
          getValues={getValues}
          title={t('Brands Sold')}
          disableGroupSelector
          control={control}
          cols={4}
          labels={brandsLabels}
          readOnly
          setValue={setValue}
          watch={watch}
          items={brandsItems}
        />
      </Grid>
    </Grid>
  );

  const renderOpenForBusiness = () => (
    <Panel title={t('Open for Business')}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="open_for_business"
            control={control}
            render={(item) => (
              <Switch
                labelPosition="left"
                label={t('Open for Business')}
                checked={item.field.value === true}
                readOnly={readOnly}
                disabled={!permissions.allowsAction('update_open_for_business.update')}
                color="primary"
                uncheckedLabel={t('No')}
                checkedLabel={t('Yes')}
                onChange={(e, newValue) => item.field.onChange(newValue)}
              />
            )}
          />
        </Grid>
      </Grid>
    </Panel>
  );

  const renderHoursOfOperations = () => {
    const google_places_id = watch('google_places_id') || null;

    return (
      <Panel title={t('Hours of Operations')}>
        <Grid item xs={12} md={12} style={{ paddingTop: '38px' }}>
          <Controller
            name="hours_of_operation"
            control={control}
            render={(item) => (
              <HoursOfOperations
                googlePlacesId={google_places_id || ''}
                googlePlacesInfo={googlePlacesInfo || undefined}
                googlePlacesInfoOnChange={(newGooglePlacesInfo: GooglePlacesInfo) => {
                  setGooglePlacesInfo(newGooglePlacesInfo);
                }}
                onChange={(newHoo: HoursOfOperation) => item.field.onChange(newHoo)}
                hoursOfOperation={item.field.value || {} as HoursOfOperation}
                readOnly={readOnly}
                country={country}
              />
            )}
          />
        </Grid>
      </Panel>
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <GenericActionButtons
          isDirty={formState.isDirty}
          isSaving={formState.isSubmitting || mutateLocation.isLoading || mutateGeoLocation.isLoading}
          onSave={!formState.isDirty ? () => {} : handleSubmit(save, onError)}
          validateAction="location_information.update"
          readOnly={readOnly && !mutateGeoLocation.isLoading}
        />
        <Grid item xs={12} md={12}>
          {renderPhysicalAddressInformationPanel()}
        </Grid>
        <Grid item xs={12} md={7}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              {renderMailingAddressInformationPanel()}
            </Grid>
            <Grid item xs={12} md={12}>
              {renderContactInformationPanel()}
            </Grid>
            <Grid item xs={12} md={12}>
              {renderMichelinIntegrations()}
            </Grid>
            <Grid item xs={12} md={12}>
              {renderPOSDetails()}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={5} style={{ marginBottom: 25 }}>
          <Grid item xs={12} md={12} style={{ marginBottom: 25 }}>
            {renderBrandsSold()}
          </Grid>
          <Grid item xs={12} md={12} style={{ marginBottom: 25 }}>
            {renderOpenForBusiness()}
          </Grid>
          <Grid item xs={12} md={12}>
            {renderHoursOfOperations()}
          </Grid>
        </Grid>
        <Grid item xs={12}>{/* Empty grid for padding bottom */}</Grid>
      </Grid>
      <PreventTransitionPrompt
        when={formState.isDirty}
      />
    </>
  );
}

interface LocationDetailsProps {
  action: 'edit' | 'view' | 'add',
  location: DealerProfileLocation,
  customer: Customer,
}

export default LocationPanel;
